/**
 * Workspace notification hooks (PR3) — the bell's second source.
 *
 * Thin TanStack Query layer over `workNotificationsApi` (src/lib/api.ts),
 * mirroring announcements.ts. The list query is gated by the caller
 * (`enabled: canViewStaffTools(whoami)`) so a user without the bell floor
 * never fires a request that would 403. Mark-seen invalidates the list so the
 * badge reflects the change immediately.
 *
 * Server is authoritative on both reads (overdue computed + unread_count) and
 * the seen write; these hooks carry no SQL/authorization logic. Own query key
 * (NOT shared with announcements) — the bell sums the two sources' counts.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  workNotificationsApi,
  type WorkNotificationWire,
  type OverdueItemWire,
} from '@/lib/api';

const WORK_NOTIFICATIONS_STALE_MS = 60_000;

export function workNotificationsQueryKey(slug: string): readonly unknown[] {
  return ['work-notifications', slug];
}

export interface UseWorkNotificationsResult {
  notifications: readonly WorkNotificationWire[];
  overdue: readonly OverdueItemWire[];
  /** Server-authoritative badge (overdue + unseen-stored-not-overdue); 0 if unknown. */
  unreadCount: number;
  isLoading: boolean;
  isError: boolean;
}

/**
 * List the caller's workspace notifications (stored feed + computed overdue +
 * badge count). Pass `enabled: false` (e.g. when the user lacks
 * view_staff_tools) to keep the hook above a capability gate without firing.
 */
export function useWorkNotifications(
  slug: string,
  opts: { enabled?: boolean } = {},
): UseWorkNotificationsResult {
  const query = useQuery({
    queryKey: workNotificationsQueryKey(slug),
    queryFn: () => workNotificationsApi.list(slug),
    staleTime: WORK_NOTIFICATIONS_STALE_MS,
    enabled: opts.enabled ?? true,
  });
  return {
    notifications: query.data?.notifications ?? [],
    overdue: query.data?.overdue ?? [],
    unreadCount: query.data?.unread_count ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * Mark the caller's unseen stored notifications seen (clears the stored part of
 * the badge; overdue persists). Invalidates the list so the count refetches.
 */
export function useMarkWorkNotificationsSeen(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => workNotificationsApi.markSeen(slug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workNotificationsQueryKey(slug) });
    },
  });
}
